import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { API, api } from 'modules/api';
import { clearCache } from 'modules/utils/utils';
import { EventsActions, EventsCategories } from 'modules/gtm/constants';
import { ReactComponent as Bookmarked } from 'modules/theme/icons/misc/card-bookmarked.svg';
import { ReactComponent as Like } from 'modules/theme/icons/misc/card-no-like.svg';
import { ReactComponent as Liked } from 'modules/theme/icons/misc/card-like.svg';
import { ReactComponent as NoBookmarked } from 'modules/theme/icons/misc/card-no-bookmarked.svg';
import { TriggerGTMEvent } from 'modules/gtm';
import * as Button from 'components/Button';

import styles from './styles.module.scss';

type SocialProps = {
  bookmarked: boolean;
  className?: string;
  contentTypeUid: string;
  handleBookmarkClick?: (contentTypeUid: string) => void;
  liked: boolean;
  likes: number;
  pageSlug: string;
};

const Social: React.FunctionComponent<SocialProps> = ({
  bookmarked,
  className,
  contentTypeUid,
  handleBookmarkClick,
  liked,
  likes = 0,
  pageSlug,
}) => {
  const [cardLiked, setCardLiked] = useState(liked);
  const [cardLikes, setCardLike] = useState(likes);
  const [cardBookmarked, setCardBookmarked] = useState(bookmarked);
  const active = useRef(true);

  useEffect(() => {
    setCardBookmarked(bookmarked);
    setCardLiked(liked);
    setCardLike(likes);
  }, [bookmarked, contentTypeUid, liked, likes]);

  //TODO done useCallback
  const postLike = useCallback(
    async (newLikeValue: boolean) => {
      try {
        setCardLiked(newLikeValue);
        if (newLikeValue) {
          setCardLike(cardLikes + 1);
        } else {
          setCardLike(cardLikes - 1);
        }
        await api(
          API.POST_LIKE_CONTENT_TYPE({
            ContentTypeUid: contentTypeUid,
            Liked: newLikeValue,
          }),
        );
        if (active.current) {
          clearCache();
          if (newLikeValue) {
            TriggerGTMEvent({
              action: EventsActions.LIKE_CONTENT_ITEMS,
              category: EventsCategories.CONTENT,
              label: pageSlug,
            });
          }
        }
      } catch (error) {
        if (active.current) {
          setCardLiked(!newLikeValue);
          if (newLikeValue) {
            setCardLike(cardLikes - 1);
          } else {
            setCardLike(cardLikes + 1);
          }
        }
      }
    },
    [cardLikes, contentTypeUid, pageSlug],
  );

  //TODO done useCallback
  const postBookmark = useCallback(
    async (newBookmarkValue: boolean) => {
      try {
        setCardBookmarked(newBookmarkValue);
        await api(
          API.POST_BOOKMARK_CONTENT_TYPE({
            ContentTypeUid: contentTypeUid,
            Liked: newBookmarkValue,
          }),
        );
        if (active.current) {
          clearCache();
          if (newBookmarkValue) {
            TriggerGTMEvent({
              action: EventsActions.BOOKMARK_CONTENT_ITEMS,
              category: EventsCategories.CONTENT,
              label: pageSlug,
            });
          }
          if (!newBookmarkValue && handleBookmarkClick) {
            handleBookmarkClick(contentTypeUid);
          }
        }
      } catch (error) {
        active.current && setCardBookmarked(!newBookmarkValue);
      }
    },
    [contentTypeUid, handleBookmarkClick, pageSlug],
  );

  useEffect(() => {
    return () => {
      active.current = false;
    };
  }, []);

  const classNames = clsx(styles.social, className);

  return (
    <div className={classNames}>
      <div className={styles.bookmark}>
        {cardBookmarked ? (
          <Button.Generic
            ariaLabel="Remove this article from bookmarks"
            className={styles.bookmarkIcon}
            onClick={() => postBookmark(false)}
            title="Remove this article from bookmarks"
          >
            <Bookmarked />
          </Button.Generic>
        ) : (
          <Button.Generic
            ariaLabel="Add this article to bookmarks"
            className={styles.bookmarkIcon}
            onClick={() => postBookmark(true)}
            title="Add this article to bookmarks"
          >
            <NoBookmarked />
          </Button.Generic>
        )}
      </div>
      <div className={styles.likes}>
        {cardLiked ? (
          <Button.Generic
            ariaLabel="Unlike this article"
            onClick={() => postLike(false)}
            title="Unlike this article"
          >
            <Liked />
          </Button.Generic>
        ) : (
          <Button.Generic
            ariaLabel="Like this article"
            onClick={() => postLike(true)}
            title="Like this article"
          >
            <Like />
          </Button.Generic>
        )}
        {cardLikes > 0 && (
          <span>
            {cardLikes > 1 ? `${cardLikes} Likes` : `${cardLikes} Like`}
          </span>
        )}
      </div>
    </div>
  );
};

export default Social;
